import {
	createSelector,
	createFeatureSelector,
	ActionReducerMap
} from '@ngrx/store';

import * as fromAuth from './auth';
import * as fromLogin from './login';
import * as fromRegister from './register';
import * as fromChangePassword from './change-password';
import * as fromForgotPassword from './forgot-password';

export interface AuthState {
	status: fromAuth.State;
	login: fromLogin.State;
	register: fromRegister.State;
	changePassword: fromChangePassword.State;
	forgotPassword: fromForgotPassword.State;
}

export interface State {
	auth: AuthState;
}

export const reducers: ActionReducerMap<AuthState> = {
	status: fromAuth.reducer,
	login: fromLogin.reducer,
	register: fromRegister.reducer,
	changePassword: fromChangePassword.reducer,
	forgotPassword: fromForgotPassword.reducer
};

export const selectAuthState = createFeatureSelector<AuthState>('auth');
export const selectAuthStatusState = createSelector(
	selectAuthState,
	(state: AuthState) => state.status
);
export const selectChangePasswordState = createSelector(
	selectAuthState,
	(state: AuthState) => state.changePassword
);
export const selectForgotPasswordState = createSelector(
	selectAuthState,
	(state: AuthState) => state.forgotPassword
);

export const getUser = createSelector(
	selectAuthStatusState,
	fromAuth.getUser,
);
export const getLogout = createSelector(
	selectAuthStatusState,
	fromAuth.getLogout,
);
export const getAuthError = createSelector(
	selectAuthStatusState,
	fromAuth.getError,
);
export const getLoggedIn = createSelector(
	selectAuthStatusState,
	fromAuth.getLoggedIn,
);
export const getLanguageSelected = createSelector(
	selectAuthStatusState,
	fromAuth.getLanguageSelected,
);
export const getLoginWithToken = createSelector(
	selectAuthStatusState,
	fromAuth.getLoginWithToken,
);
export const getAccessToken = createSelector(
	selectAuthStatusState,
	fromAuth.getAccessToken,
);
export const getRefreshToken = createSelector(
	selectAuthStatusState,
	fromAuth.getRefreshToken,
);
export const getExpiresIn = createSelector(
	selectAuthStatusState,
	fromAuth.getExpiresIn,
);

export const getUpdated = createSelector(
	selectAuthStatusState,
	fromAuth.getUpdated,
);

export const getUpdatePending = createSelector(
	selectAuthStatusState,
	fromAuth.getUpdatePending,
);

export const selectLoginState = createSelector(
	selectAuthState,
	(state: AuthState) => state.login,
);
export const selectRegisterState = createSelector(
	selectAuthState,
	(state: AuthState) => state.register,
);
export const getLoginRequired = createSelector(
	selectLoginState,
	fromLogin.getLoginRequired,
);
export const getLoginMessage = createSelector(
	selectLoginState,
	fromLogin.getMessage,
);
export const getLoginError = createSelector(
	selectLoginState,
	fromLogin.getError,
);
export const getLoginPending = createSelector(
	selectLoginState,
	fromLogin.getPending,
);
export const getRegisterError = createSelector(
	selectRegisterState,
	fromRegister.getError,
);
export const getRegisterMessage = createSelector(
	selectRegisterState,
	fromRegister.getMessage,
);
export const getRegisterPending = createSelector(
	selectRegisterState,
	fromRegister.getPending,
);
export const getRegisterSuccess = createSelector(
	selectRegisterState,
	fromRegister.getSuccess,
);


export const getForgottenPasswordValidated = createSelector(
	selectForgotPasswordState,
	fromForgotPassword.getValidated,
);
export const getForgottenPasswordUpdated = createSelector(
	selectForgotPasswordState,
	fromForgotPassword.getPasswordUpdated,
);
export const getForgottenPasswordError = createSelector(
	selectForgotPasswordState,
	fromForgotPassword.getError,
);
export const getForgottenPasswordVerificationSent = createSelector(
	selectForgotPasswordState,
	fromForgotPassword.getVerificationSent,
);
export const getForgottenPasswordVerificationPending = createSelector(
	selectForgotPasswordState,
	fromForgotPassword.getVerificationPending,
);
export const getForgottenPasswordValidationPending = createSelector(
	selectForgotPasswordState,
	fromForgotPassword.getValidationPending,
);
export const getForgottenPasswordUpdatePending = createSelector(
	selectForgotPasswordState,
	fromForgotPassword.getPasswordUpdatePending,
);

export const getChangePasswordPending = createSelector(
	selectChangePasswordState,
	fromChangePassword.getPasswordChangePending,
);

export const getChangePasswordSuccess = createSelector(
	selectChangePasswordState,
	fromChangePassword.getPasswordChanged,
);

export const getChangePasswordError = createSelector(
	selectChangePasswordState,
	fromChangePassword.getError,
);

export const getChangeEmailPending = createSelector(
	selectAuthStatusState,
	fromAuth.getChangeEmailPending,
);
export const getChangeEmailSuccess = createSelector(
	selectAuthStatusState,
	fromAuth.getChangeEmailSuccess,
);

export const getVerifyEmailPending = createSelector(
	selectAuthStatusState,
	fromAuth.getVerifyEmailPending,
);

export const getVerifyEmailSuccess = createSelector(
	selectAuthStatusState,
	fromAuth.getVerifyEmailSuccess,
);
